// src/pages/TextTools/WordcloudGenerator.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateWordcloud } from '../../services/chatApi';
import { Download, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const WordcloudGenerator = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form parameters
    const [stopwordsLang, setStopwordsLang] = useState('english');
    const [userStopwords, setUserStopwords] = useState('');
    const [maxWords, setMaxWords] = useState(200);
    const [backgroundColor, setBackgroundColor] = useState('black');
    const [contourColor, setContourColor] = useState('steelblue');
    const [maskShape, setMaskShape] = useState('circle');

    const availableLanguages = [
        'arabic', 'azerbaijani', 'danish', 'dutch', 'english', 'finnish', 
        'french', 'german', 'greek', 'hungarian', 'indonesian', 'italian', 
        'kazakh', 'nepali', 'norwegian', 'portuguese', 'romanian', 'russian', 
        'slovene', 'spanish', 'swedish', 'tajik', 'turkish'
    ];

    const shapeOptions = [
        'circle', 'square', 'heart', 'star', 'diamond', 
        'hexagon', 'cloud', 'oval'
    ];

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError(t('wordcloud.fileTooLarge'));
                return;
            }
            setUploadedFile(file);
            setError(null);
        }
    };

    const handleGenerate = async () => {
        setError(null);
        setGeneratedImage(null);

        if (!uploadedFile) {
            setError(t('wordcloud.uploadFileFirst'));
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('stopwords_lang', stopwordsLang);
            formData.append('user_stopwords', userStopwords);
            formData.append('max_words', maxWords);
            formData.append('background_color', backgroundColor);
            formData.append('contour_color', contourColor);
            formData.append('mask_shape', maskShape);

            const result = await generateWordcloud(formData);
            
            // Direct response with image data
            setGeneratedImage(`data:image/png;base64,${result.image_data}`);
            
            // Update credits
            if (result.new_credits !== undefined) {
                console.log(`Credits updated. New balance: ${result.new_credits}`);
                setCredits(result.new_credits);
            }
            
            setIsLoading(false);
        } catch (err) {
            setError(err.message || t('wordcloud.errorGeneric'));
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!generatedImage) return;

        const link = document.createElement('a');
        link.href = generatedImage;
        link.download = 'wordcloud.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClear = () => {
        setUploadedFile(null);
        setGeneratedImage(null);
        setError(null);
        setStopwordsLang('english');
        setUserStopwords('');
        setMaxWords(200);
        setBackgroundColor('black');
        setContourColor('steelblue');
        setMaskShape('circle');
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('wordcloud.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('wordcloud.description')}
                    </p>

                    {/* File Upload Section */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium mb-2">
                            {t('wordcloud.fileLabel')}
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-brand-orange transition-colors">
                                <Upload className="mr-2" size={20} />
                                <span>{uploadedFile ? uploadedFile.name : t('wordcloud.chooseFile')}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.doc,.txt"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    disabled={isLoading}
                                />
                            </label>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                            {t('wordcloud.fileFormats')}
                        </p>
                    </div>

                    {/* Parameters Grid */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Language Selection */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t('wordcloud.stopwordsLanguage')}
                            </label>
                            <select
                                value={stopwordsLang}
                                onChange={(e) => setStopwordsLang(e.target.value)}
                                className="w-full p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                disabled={isLoading}
                            >
                                {availableLanguages.map(lang => (
                                    <option key={lang} value={lang}>{lang.charAt(0).toUpperCase() + lang.slice(1)}</option>
                                ))}
                            </select>
                        </div>

                        {/* Shape Selection */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t('wordcloud.shapeLabel')}
                            </label>
                            <select
                                value={maskShape}
                                onChange={(e) => setMaskShape(e.target.value)}
                                className="w-full p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                disabled={isLoading}
                            >
                                {shapeOptions.map(shape => (
                                    <option key={shape} value={shape}>{shape.charAt(0).toUpperCase() + shape.slice(1)}</option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-600 mt-1">
                                {t('wordcloud.stopwordsHintUse')}
                            </p>
                        </div>

                        {/* Background Color */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t('wordcloud.backgroundColor')}
                            </label>
                            <input
                                type="text"
                                value={backgroundColor}
                                onChange={(e) => setBackgroundColor(e.target.value)}
                                placeholder="e.g., black, #FFFFFF, rgb(255,255,255)"
                                className="w-full p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                disabled={isLoading}
                            />
                            <p className="text-xs text-slate-600 mt-1">
                                {t('wordcloud.colorHint')}
                            </p>
                        </div>

                        {/* Contour Color */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                {t('wordcloud.contourColor')}
                            </label>
                            <input
                                type="text"
                                value={contourColor}
                                onChange={(e) => setContourColor(e.target.value)}
                                placeholder="e.g., steelblue, #4682B4"
                                className="w-full p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    {/* Max Words Slider */}
                    <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">
                            {t('wordcloud.maxWords')}: {maxWords}
                        </label>
                        <input
                            type="range"
                            min="50"
                            max="500"
                            value={maxWords}
                            onChange={(e) => setMaxWords(parseInt(e.target.value))}
                            className="w-full"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Custom Stopwords */}
                    <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">
                            {t('wordcloud.customStopwords')}
                        </label>
                        <input
                            type="text"
                            value={userStopwords}
                            onChange={(e) => setUserStopwords(e.target.value)}
                            placeholder={t('wordcloud.stopwordsPlaceholder')}
                            className="w-full p-2 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none rounded-md"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-slate-600 mt-1">
                            {t('wordcloud.stopwordsHint')}
                        </p>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('wordcloud.generatingButton')}
                                </>
                            ) : (
                                <>
                                    <ImageIcon className="mr-2" size={20} />
                                    {t('wordcloud.generateButton')}
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            disabled={isLoading}
                        >
                            {t('wordcloud.clearButton')}
                        </button>
                    </div>

                    {/* Display Generated Wordcloud */}
                    {generatedImage && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold">
                                    {t('wordcloud.generatedWordcloud')}
                                </h4>
                            </div>
                            
                            <div className="relative border border-slate-300 rounded-md overflow-hidden bg-white">
                                <img 
                                    src={generatedImage} 
                                    alt="Generated Wordcloud" 
                                    className="w-full h-auto"
                                />
                            </div>

                            {/* Download Button */}
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('wordcloud.downloadPNG')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WordcloudGenerator;
